/**
 * Centralized country direction logic.
 * Supports configured Europe ↔ Africa routes and intra-Europe shipping.
 * Ensures only valid routes are selectable.
 * Provides translateCountry/translateCity helpers for display.
 *
 * Merges courier-sourced route cities into the available city list
 * so customers can book to any city a courier has added to an active route.
 */
import { useMemo, useCallback } from "react";
import { useLocations } from "@/hooks/useLocations";
import { useRouteCoveredCities } from "@/hooks/useRouteCoveredCities";
import { useTranslation } from "react-i18next";

/**
 * African corridor side. Countries on this side ship to/from Europe; Europe
 * also ships intra-Europe. Derived from the ISO codes of active countries so
 * new corridors (Tunisia, Algeria, ...) work without a code change.
 */
import { AFRICA_ISO } from "@/lib/corridorSides";

export const useCountryDirectionLogic = () => {
  const { countryCities: baseCities, countries, loading: locLoading } = useLocations();
  const { routeCities, loading: routeLoading } = useRouteCoveredCities();
  const { t } = useTranslation();

  // Merge base (active) cities with route-covered cities
  const countryCities = useMemo(() => {
    const merged: Record<string, string[]> = {};

    // Start with base cities
    for (const [country, cities] of Object.entries(baseCities)) {
      merged[country] = [...cities];
    }

    // Add route-covered cities that aren't already present
    for (const [country, cities] of Object.entries(routeCities)) {
      if (!merged[country]) merged[country] = [];
      const existing = new Set(merged[country]);
      for (const city of cities) {
        if (!existing.has(city)) {
          merged[country].push(city);
        }
      }
      merged[country].sort();
    }

    return merged;
  }, [baseCities, routeCities]);

  const loading = locLoading || routeLoading;

  const translateCountry = useCallback(
    (name?: string | null) =>
      name && name.trim() ? t(`locations.countries.${name}`, { defaultValue: name }) : "",
    [t]
  );

  const translateCity = useCallback(
    (name?: string | null) =>
      name && name.trim() ? t(`locations.cities.${name}`, { defaultValue: name }) : "",
    [t]
  );

  const allCountryNames = useMemo(
    () => Object.keys(countryCities),
    [countryCities]
  );

  const africanCountries = useMemo(
    () =>
      countries
        .filter((c) => AFRICA_ISO.has((c.isoCode || "").toUpperCase()))
        .map((c) => c.name),
    [countries]
  );

  const europeanCountries = useMemo(
    () => allCountryNames.filter((c) => !africanCountries.includes(c)),
    [allCountryNames, africanCountries]
  );

  const isAfrican = (country: string) => africanCountries.includes(country);
  /** @deprecated corridor-side checks must use isAfrican(); kept for callers not yet migrated. */
  const isMorocco = (country: string) => isAfrican(country);
  const isEuropean = (country: string) =>
    europeanCountries.includes(country);

  /**
   * Given the selected pickup country, return the allowed
   * delivery countries (with their cities).
   */
  const getDeliveryCountries = (
    pickupCountry: string
  ): Record<string, string[]> => {
    if (!pickupCountry) return {};

    if (isAfrican(pickupCountry)) {
      const result: Record<string, string[]> = {};
      for (const c of europeanCountries) {
        if (countryCities[c]?.length) result[c] = countryCities[c];
      }
      return result;
    }

    if (isEuropean(pickupCountry)) {
      const result: Record<string, string[]> = {};
      
      for (const c of africanCountries) {
        if (countryCities[c]?.length) result[c] = countryCities[c];
      }
      
      for (const c of europeanCountries) {
        if (c !== pickupCountry && countryCities[c]?.length) {
          result[c] = countryCities[c];
        }
      }
      
      return result;
    }

    return {};
  };

  /**
   * All valid pickup countries (all active countries).
   */
  const getPickupCountries = (): Record<string, string[]> => {
    const result: Record<string, string[]> = {};
    for (const [country, cities] of Object.entries(countryCities)) {
      if (cities?.length) result[country] = cities;
    }
    return result;
  };

  return {
    loading,
    countryCities,
    countries,
    europeanCountries,
    africanCountries,
    getPickupCountries,
    getDeliveryCountries,
    isMorocco,
    isAfrican,
    isEuropean,
    translateCountry,
    translateCity,
  };
};
